import { api } from '../../api';

export type ResumeStatus = 'draft' | 'published' | 'hidden';

export type ResumeMeta = {
  name: string;
  grade: string;
  program: string;
  direction: string;
  summary: string;
  portrait: string | null;
  thumb: string | null;
  galleryCount: number;
  links: Array<{ label: string; url: string }>;
};

export type ResumeEducation = {
  school: string;
  degree: string;
  major: string;
  start: string;
  end: string;
};

export type ResumeUploadRef = {
  id: string;
  name: string;
  mime: string;
};

export type ResumeProject = {
  name: string;
  role: string;
  period: string;
  description: string;
  photo: ResumeUploadRef | null;
};

export type ResumeConference = {
  name: string;
  topic: string;
  role: string;
  period: string;
  location: string;
  description: string;
  photo: ResumeUploadRef | null;
};

export type ResumeInternship = {
  company: string;
  role: string;
  period: string;
  description: string;
  photo: ResumeUploadRef | null;
};

export type ResumeAchievement = {
  title: string;
  date: string;
  description: string;
  material: ResumeUploadRef | null;
};

export type ResumeProfile = {
  user_id: number;
  slug: string;
  display_name: string;
  grade: string;
  program: string;
  direction: string;
  summary: string;
  portrait: ResumeUploadRef | null;
  education: ResumeEducation[];
  skills: string[];
  projects: ResumeProject[];
  conferences: ResumeConference[];
  internships: ResumeInternship[];
  achievements: ResumeAchievement[];
  email: string | null;
  showEmail: boolean;
  isCurrent: boolean;
  canPublish: boolean;
  autoApprove: boolean;
  status: ResumeStatus;
  publishedMeta: ResumeMeta | null;
  updated_at: string;
  published_at: string | null;
};

export type TemplateInfo = {
  id: string;
  name: string;
  summary: string;
  layout: 'tall' | 'wide' | 'short';
  sampleName: string;
  sampleGrade: string;
  sampleDirection: string;
  highlights: string[];
  index: string;
};

export type GrantStatus = {
  active: boolean;
  expiresAt: string | null;
  successCount: number;
  maxPublishes: number;
  attemptCount: number;
  maxAttempts: number;
};

export type PublishSummary = {
  id: string;
  version: string;
  status: 'pending' | 'published' | 'rejected' | 'superseded' | 'hidden';
  reviewNote: string | null;
  createdAt: string;
  publishedAt: string | null;
  fileCount: number;
  bytes: number;
};

export type ResumeMe = {
  profile: ResumeProfile | null;
  grant: GrantStatus | null;
  templates: TemplateInfo[];
  publishes: PublishSummary[];
  pending: { id: string; version: string; created_at: string } | null;
};

export type AdminPublish = {
  id: string;
  version: string;
  status: PublishSummary['status'];
  createdAt: string;
  publishedAt: string | null;
};

export type AdminProfile = ResumeProfile & { publishes: AdminPublish[] };

export type PendingRow = {
  id: string;
  slug: string;
  user_id: number;
  version: string;
  created_at: string;
  manifest: { meta?: ResumeMeta } | null;
};

export type AdminData = { profiles: AdminProfile[]; pending: PendingRow[] };

export type DirectoryMember = {
  slug: string;
  isCurrent: boolean;
  status: ResumeStatus;
  locked: boolean;
  isSelf: boolean;
  pending: boolean;
  pendingId: string | null;
  meta: ResumeMeta;
};

export type DirectoryData = { members: DirectoryMember[] };

export const fetchMe = () => api<ResumeMe>('/api/resume/me');

export const fetchDirectory = () => api<DirectoryData>('/api/resume/directory');

export const saveProfile = (body: {
  displayName?: string;
  grade?: string;
  program?: string;
  direction?: string;
  summary?: string;
  portrait?: ResumeUploadRef | null;
  education?: ResumeEducation[];
  skills?: string[];
  projects?: ResumeProject[];
  conferences?: ResumeConference[];
  internships?: ResumeInternship[];
  achievements?: ResumeAchievement[];
  email?: string;
  showEmail?: boolean;
}) =>
  api<{ profile: ResumeProfile }>('/api/resume/me', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const uploadUrl = (id: string) => `/api/resume/uploads/${encodeURIComponent(id)}`;

const UPLOAD_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  pdf: 'application/pdf',
};

export const uploadResumeFile = (kind: string, file: File) => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  const type = file.type || UPLOAD_TYPES[extension] || 'application/octet-stream';
  const query = new URLSearchParams({ kind, name: file.name });
  return api<{ upload: ResumeUploadRef & { kind: string; bytes: number; url: string; createdAt: string } }>(
    `/api/resume/uploads?${query.toString()}`,
    { method: 'POST', headers: { 'Content-Type': type }, body: file },
  );
};

export const issueGrant = () =>
  api<{ token: string; expiresAt: string; maxPublishes: number; maxAttempts: number }>(
    '/api/resume/grant',
    { method: 'POST' },
  );

export const fetchAdmin = () => api<AdminData>('/api/admin/resumes');

export const saveEligibility = (
  userId: number,
  body: { slug: string; canPublish: boolean; autoApprove: boolean; isCurrent?: boolean },
) =>
  api<{ profile: ResumeProfile }>(`/api/admin/resumes/${userId}/eligibility`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const approvePublish = (publishId: string) =>
  api(`/api/admin/resumes/${publishId}/approve`, { method: 'POST' });

export const rejectPublish = (publishId: string, note: string) =>
  api(`/api/admin/resumes/${publishId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });

export const hideResume = (userId: number) =>
  api(`/api/admin/resumes/${userId}/hide`, { method: 'POST' });

export const unhideResume = (userId: number) =>
  api(`/api/admin/resumes/${userId}/unhide`, { method: 'POST' });

export const rollbackResume = (userId: number, publishId: string) =>
  api(`/api/admin/resumes/${userId}/rollback`, {
    method: 'POST',
    body: JSON.stringify({ publishId }),
  });

export const revokeUserGrant = (userId: number) =>
  api(`/api/admin/resumes/${userId}/grant`, { method: 'DELETE' });

export function buildPrompt(input: {
  profile: ResumeProfile;
  template: TemplateInfo;
  slug: string;
  token: string;
  origin: string;
}): string {
  const { profile, template, slug, token, origin } = input;
  const assetUrl = (id: string) =>
    `${origin}/api/resume/uploads/${encodeURIComponent(id)}?grant=${encodeURIComponent(token)}`;
  const withUpload = (ref: ResumeUploadRef | null) =>
    ref ? { id: ref.id, name: ref.name, mime: ref.mime, url: assetUrl(ref.id) } : undefined;
  const data = {
    name: profile.display_name,
    grade: profile.grade,
    program: profile.program,
    direction: profile.direction,
    summary: profile.summary,
    email: profile.showEmail ? profile.email : undefined,
    showEmail: profile.showEmail,
    portrait: withUpload(profile.portrait),
    education: profile.education.length ? profile.education : undefined,
    skills: profile.skills.length ? profile.skills : undefined,
    projects: profile.projects.length
      ? profile.projects.map((item) => ({ ...item, photo: withUpload(item.photo) }))
      : undefined,
    conferences: profile.conferences.length
      ? profile.conferences.map((item) => ({ ...item, photo: withUpload(item.photo) }))
      : undefined,
    internships: profile.internships.length
      ? profile.internships.map((item) => ({ ...item, photo: withUpload(item.photo) }))
      : undefined,
    achievements: profile.achievements.length
      ? profile.achievements.map((item) => ({ ...item, material: withUpload(item.material) }))
      : undefined,
  };
  const attachments: string[] = [];
  if (profile.portrait) attachments.push(`个人照片：${assetUrl(profile.portrait.id)}`);
  profile.projects.forEach((item, index) => {
    if (item.photo) attachments.push(`科研项目「${item.name || index + 1}」照片：${assetUrl(item.photo.id)}`);
  });
  profile.conferences.forEach((item, index) => {
    if (item.photo) attachments.push(`会议「${item.name || index + 1}」照片：${assetUrl(item.photo.id)}`);
  });
  profile.internships.forEach((item, index) => {
    if (item.photo) attachments.push(`实习「${item.company || index + 1}」照片：${assetUrl(item.photo.id)}`);
  });
  profile.achievements.forEach((item, index) => {
    if (item.material) {
      attachments.push(`成果「${item.title || index + 1}」材料：${assetUrl(item.material.id)}（${item.material.mime}）`);
    }
  });
  const publishUrl = `${origin}/api/resume/publish?slug=${encodeURIComponent(slug)}`;
  return `请帮我做一份静态简历网页，最后打包成一个 zip。

【模板】
模板名：${template.name}
模板 zip（请先下载，作为起点）：${origin}/api/resume/templates/${template.id}/zip
在线预览：${origin}/resume/_templates/${template.index}
如果你不能联网下载，我会把模板 zip 发给你。

【我的资料】
${JSON.stringify(data, null, 2)}

【照片与材料】
${attachments.length ? attachments.join('\n') : '（没有上传照片或材料）'}
- 需要时用 curl -L "上面的地址" -o 文件名 下载；这些地址已经带权限，不要发到公开渠道。
- 图片放到 assets/ 并在 index.html 用相对路径引用（必须有 alt）；成果材料是 PDF 时放到 materials/ 并用相对链接提供下载。
- 单张图片 ≤ 1.5 MB、≤ 6000 px；PDF ≤ 4 MB；整个 zip ≤ 8 MB。

【zip 里放什么】
- index.html：只能有一个，开头要有 <meta charset="utf-8">、手机端 viewport、<title>，正文有一个 <h1>
- resume.json：字段为 name、grade、program、direction、summary、showEmail、email（仅 showEmail=true 时）、portrait、thumb、gallery、links
- 教育背景、技能证书、科研项目、会议参加、实习经历、成果请排进 index.html 正文；会议条目中 topic 是汇报题目、role 是参与形式、period 是参会时间；照片放进对应栏目，没有的栏目直接省略
- style.css 和 assets/：放样式和图片；图片只允许 jpg/jpeg/png/webp，成果材料允许 pdf
- 头像 ≤ 1.5 MB；缩略图 ≤ 100 KB 且 ≤ 800×800；整个 zip ≤ 8 MB

【必须遵守】
- 不要 JavaScript、<script>、iframe、form、SVG、外链字体/图片/样式
- 图片和链接只用 zip 里的相对路径；<a> 外链只允许 https
- 手机能正常阅读，打印也好看；不要编造论文、学位、单位或奖项
- 图片要写 alt；没有照片就用名字首字做一个抽象头像
- 如果 showEmail=false，任何文件里都不要出现邮箱
- 把模板里的“示例”字样和示例姓名换成我的资料

【做好后，用你能做到的方式提交】
1. 如果你能联网并执行命令：把 zip 存成 resume.zip，运行下面这行，简历就会提交（可能直接上线，也可能进入管理员审核）：
curl -X POST '${publishUrl}' -H "Authorization: Bearer ${token}" -H "Content-Type: application/zip" --data-binary @resume.zip
这串密钥只对我这份简历有效，30 分钟内有效，最多成功 3 次，不要发到公开渠道。
2. 如果你不能联网或不能执行命令：直接说明是哪一步做不到，不要假装已经上传成功。`;
}